import { supabase } from "../lib/supabase";
import { openai } from "../lib/openai";
import { SitemapService } from "./sitemapService";
import { getOpenAIApiKey, isOpenAIAvailable } from "../config/runtime-config";

export interface SEOOptimizationResult {
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string[];
  excerpt: string;
  featuredImage: string;
  cta: string;
  localSEO: {
    location: string;
    serviceArea: string[];
    localKeywords: string[];
  };
  schemaMarkup: any;
  faqs: Array<{
    question: string;
    answer: string;
  }>;
  contentOptimization: {
    suggestions: string[];
    improvedContent: string;
  };
  // Author Schema Enhancement
  authorSchema: {
    name: string;
    jobTitle: string;
    bio: string;
    imageUrl: string;
    email: string;
    phone: string;
    linkedin: string;
    github: string;
    instagram: string;
    education: string;
    yearsExperience: {
      ai: number;
      projectManagement: number;
      softwareDevelopment: number;
    };
    expertise: string[];
  };
  // Advanced SEO Features
  aeoOptimization: {
    answerBoxes: Array<{
      question: string;
      answer: string;
      confidence: number;
    }>;
    voiceSearchQueries: string[];
    conversationalKeywords: string[];
  };
  accessibilityOptimization: {
    altTextSuggestions: string[];
    headingStructure: Array<{
      level: number;
      text: string;
      suggestedText: string;
    }>;
    ariaLabels: Array<{
      element: string;
      label: string;
    }>;
    colorContrastSuggestions: string[];
    keyboardNavigationTips: string[];
  };
  aiSearchOptimization: {
    semanticKeywords: string[];
    entityMentions: Array<{
      entity: string;
      type: string;
      context: string;
    }>;
    topicClusters: Array<{
      mainTopic: string;
      subtopics: string[];
      relatedKeywords: string[];
    }>;
    intentMatching: {
      informational: string[];
      navigational: string[];
      transactional: string[];
      commercial: string[];
    };
  };
  technicalSEO: {
    structuredDataTypes: string[];
    internalLinkingSuggestions: Array<{
      anchorText: string;
      targetUrl: string;
      relevance: string;
    }>;
    imageOptimization: {
      suggestedAltText: string[];
      compressionTips: string[];
      formatRecommendations: string[];
    };
    performanceOptimization: {
      suggestions: string[];
      criticalPathOptimizations: string[];
    };
  };
}

export class SEOOptimizationService {
  static async optimizeBlogPost(
    title: string,
    content: string,
    currentExcerpt: string = "",
    currentMetaDescription: string = "",
    location: string = "Omaha, Nebraska",
  ): Promise<SEOOptimizationResult> {
    try {
      // Check if OpenAI API key is configured using runtime config
      if (!isOpenAIAvailable()) {
        throw new Error(
          "OpenAI API key not configured. Please add VITE_OPENAI_API_KEY to your environment variables or use the 'Set API Key' option.",
        );
      }
      // Get the API key and create a new OpenAI client
      const apiKey = getOpenAIApiKey();
      const openaiClient = new (await import("openai")).default({
        apiKey: apiKey,
        dangerouslyAllowBrowser: true,
      });

      const prompt = `You are an expert SEO specialist for One80Labs, a premier AI agency in Omaha, Nebraska. 

IMPORTANT: You must respond with ONLY valid JSON. Do not include any text before or after the JSON object.

Blog Post Title: ${title}
Blog Post Content: ${content}
Current Excerpt: ${currentExcerpt}
Current Meta Description: ${currentMetaDescription}

Create comprehensive SEO optimization for this blog post. Focus on AI, technology, Omaha/Nebraska/Midwest, and business terms. IMPORTANT: Do NOT rewrite the content - only provide SEO suggestions and minor enhancements. For featuredImage, if you can't provide a real image URL, just describe the ideal image type.

Respond with ONLY a JSON object in this exact format (no other text):

{
  "metaTitle": "SEO-optimized title under 60 characters",
  "metaDescription": "SEO-optimized description under 160 characters", 
  "metaKeywords": ["AI", "technology", "Omaha", "Midwest", "business"],
  "excerpt": "SEO-optimized excerpt under 160 characters",
  "featuredImage": "URL or description of ideal featured image (prefer existing image URLs)",
  "cta": "Call-to-action suggestion",
  "localSEO": {
    "location": "Omaha, Nebraska",
    "serviceArea": ["Omaha", "Council Bluffs", "Midwest"],
    "localKeywords": ["Omaha AI", "Nebraska technology", "Midwest business"]
  },
  "schemaMarkup": {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Article headline",
    "description": "Article description"
  },
  "faqs": [
    {
      "question": "Relevant FAQ question",
      "answer": "Helpful answer"
    }
  ],
  "contentOptimization": {
    "suggestions": ["Content improvement suggestions - focus on SEO, not rewriting"],
    "improvedContent": "Original content with minor SEO enhancements only"
  },
  "aeoOptimization": {
    "answerBoxes": [
      {
        "question": "Question for featured snippet",
        "answer": "Direct answer",
        "confidence": 0.9
      }
    ],
    "voiceSearchQueries": ["Voice search optimized queries"],
    "conversationalKeywords": ["Conversational keywords"]
  },
  "accessibilityOptimization": {
    "altTextSuggestions": ["Alt text suggestions"],
    "headingStructure": [
      {
        "level": 2,
        "text": "Current heading",
        "suggestedText": "Improved heading"
      }
    ],
    "ariaLabels": [
      {
        "element": "button",
        "label": "Accessible label"
      }
    ],
    "colorContrastSuggestions": ["Color contrast tips"],
    "keyboardNavigationTips": ["Keyboard navigation tips"]
  },
  "aiSearchOptimization": {
    "semanticKeywords": ["Semantic keywords"],
    "entityMentions": [
      {
        "entity": "Entity name",
        "type": "Entity type",
        "context": "Context description"
      }
    ],
    "topicClusters": [
      {
        "mainTopic": "Main topic",
        "subtopics": ["Subtopic 1", "Subtopic 2"],
        "relatedKeywords": ["Related keywords"]
      }
    ],
    "intentMatching": {
      "informational": ["Informational keywords"],
      "navigational": ["Navigational keywords"],
      "transactional": ["Transactional keywords"],
      "commercial": ["Commercial keywords"]
    }
  },
  "technicalSEO": {
    "structuredDataTypes": ["Article", "FAQPage"],
    "internalLinkingSuggestions": [
      {
        "anchorText": "Link text",
        "targetUrl": "/target-page",
        "relevance": "High"
      }
    ],
    "imageOptimization": {
      "suggestedAltText": ["Alt text suggestions"],
      "compressionTips": ["Image compression tips"],
      "formatRecommendations": ["WebP", "AVIF"]
    },
    "performanceOptimization": {
      "suggestions": ["Performance tips"],
      "criticalPathOptimizations": ["Critical path optimizations"]
    }
  }
}`;

      console.log("🔑 Using API key:", apiKey.substring(0, 20) + "...");
      console.log("🚀 Making OpenAI API call...");

      const completion = await openaiClient.chat.completions.create({
        model: "gpt-4",
        messages: [
          {
            role: "system",
            content:
              "You are an expert SEO specialist with deep knowledge of AI, technology, and local SEO for the Omaha/Nebraska market. You must ALWAYS respond with valid JSON only. Never include explanatory text before or after the JSON object.",
          },
          {
            role: "user",
            content: prompt,
          },
        ],
        temperature: 0.7,
        max_tokens: 2000,
      });

      console.log("✅ OpenAI API call successful");

      const response = completion.choices[0]?.message?.content;
      if (!response) {
        throw new Error("No response from AI");
      }

      console.log(
        "📄 Raw AI response (first 200 chars):",
        response.substring(0, 200),
      );

      // Try to extract JSON from the response if it contains extra text
      let jsonResponse = response;
      const jsonStart = response.indexOf("{");
      const jsonEnd = response.lastIndexOf("}");

      if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
        jsonResponse = response.substring(jsonStart, jsonEnd + 1);
        console.log("🔧 Extracted JSON from response");
      }

      // Parse the JSON response
      let optimizationResult: SEOOptimizationResult;
      try {
        optimizationResult = JSON.parse(jsonResponse) as SEOOptimizationResult;
      } catch (parseError) {
        console.error("❌ JSON parsing failed:", parseError);
        console.error("📄 Failed to parse response:", jsonResponse);
        throw new Error(
          `Invalid JSON response from AI. Response starts with: "${response.substring(0, 50)}..."`,
        );
      }

      // Add enhanced Person schema for author
      optimizationResult.authorSchema = {
        name: "Nick Kramer",
        jobTitle: "AI Consultant & Trainer, Founder, AI Automation Engineer",
        bio: "AI Consultant & Trainer specializing in ChatGPT, Gemini, and Microsoft 365 Copilot training. Founder of One80Labs with 25 years of software development experience, 15 years in project/product management, and 2 years focused on AI implementation. Based in Omaha, Nebraska, helping businesses leverage AI tools for improved productivity and workflows.",
        imageUrl:
          "https://raw.githubusercontent.com/ndkramer/One80Labs-Site/main/public/images/team/nick.kramer.png",
        email: "hello@one80labs.com",
        phone: "+1-402-915-3616",
        linkedin: "https://www.linkedin.com/in/nickolaskramer/",
        github: "https://github.com/ndkramer",
        instagram: "https://www.instagram.com/one80labs/",
        education:
          "Degree in Business Administration and Information Systems from Montana State University",
        yearsExperience: {
          ai: 2,
          projectManagement: 15,
          softwareDevelopment: 25,
        },
        expertise: [
          "Artificial Intelligence",
          "Project Management",
          "Product Management",
          "Software Development",
          "ChatGPT",
          "Gemini",
          "Microsoft 365 Copilot",
          "Claude",
          "Automation",
          "Process Improvement",
          "Lean Software Development",
        ],
      };

      console.log("✅ Enhanced Person schema added to optimization result");

      return optimizationResult;
    } catch (error) {
      console.error("❌ Error optimizing SEO:", error);

      // Provide more specific error messages
      if (error instanceof Error) {
        if (error.message.includes("401")) {
          throw new Error("Invalid API key. Please check your OpenAI API key.");
        } else if (error.message.includes("429")) {
          throw new Error("Rate limit exceeded. Please try again later.");
        } else if (error.message.includes("500")) {
          throw new Error("OpenAI server error. Please try again.");
        } else if (error.message.includes("No response from AI")) {
          throw new Error("AI response was empty. Please try again.");
        } else {
          throw new Error(`SEO optimization failed: ${error.message}`);
        }
      } else {
        throw new Error("Failed to optimize SEO");
      }
    }
  }

  static async updateSitemap(): Promise<void> {
    try {
      console.log("🔄 Triggering sitemap regeneration...");

      const result = await SitemapService.triggerSitemapRegeneration();

      if (result.success) {
        console.log("✅ Sitemap regeneration triggered successfully");
        console.log("📋 Instructions:", result.instructions?.join("\n"));
        console.log("🎯 Next steps:", result.nextSteps?.join("\n"));
      } else {
        console.error(
          "❌ Failed to trigger sitemap regeneration:",
          result.message,
        );
      }
    } catch (error) {
      console.error("Error updating sitemap:", error);
      throw new Error("Failed to update sitemap");
    }
  }

  static async updateBlogPostSEO(
    postId: string,
    optimizationResult: SEOOptimizationResult,
  ): Promise<void> {
    try {
      // Get the current blog post to preserve existing image_url
      const { data: currentPost, error: fetchError } = await supabase
        .from("blog_posts")
        .select("image_url")
        .eq("id", postId)
        .single();

      if (fetchError) {
        console.error("❌ Error fetching current post:", fetchError);
      }

      // First, try to update without the new fields in case they don't exist yet
      const updateData: any = {
        meta_title: optimizationResult.metaTitle,
        meta_description: optimizationResult.metaDescription,
        meta_keywords: optimizationResult.metaKeywords,
        excerpt: optimizationResult.excerpt,
        // Preserve existing image_url - only update if AI generated a real URL and current image is empty
        ...(currentPost?.image_url && currentPost.image_url.trim() !== ""
          ? { image_url: currentPost.image_url }
          : { image_url: optimizationResult.featuredImage }),
        // Add SEO data to database
        faqs: optimizationResult.faqs,
        schema_markup: optimizationResult.schemaMarkup,
        local_seo: optimizationResult.localSEO,
        updated_at: new Date().toISOString(),
      };

      // Add the SEO tracking fields
      updateData.seo_optimized_at = new Date().toISOString();
      updateData.seo_optimization_version = "2.0";

      console.log("💾 Saving SEO data to database:", updateData);

      const { error } = await supabase
        .from("blog_posts")
        .update(updateData)
        .eq("id", postId);

      if (error) {
        console.error("❌ Database update error:", error);
        // If the new fields don't exist, try without them
        if (
          error.message?.includes("column") &&
          error.message?.includes("does not exist")
        ) {
          console.log("🔄 Retrying without new SEO fields...");
          const { error: retryError } = await supabase
            .from("blog_posts")
            .update({
              meta_title: optimizationResult.metaTitle,
              meta_description: optimizationResult.metaDescription,
              meta_keywords: optimizationResult.metaKeywords,
              excerpt: optimizationResult.excerpt,
              // Preserve existing image_url in retry too - only update if current image is empty
              ...(currentPost?.image_url && currentPost.image_url.trim() !== ""
                ? { image_url: currentPost.image_url }
                : { image_url: optimizationResult.featuredImage }),
              // Add SEO data even in retry
              faqs: optimizationResult.faqs,
              schema_markup: optimizationResult.schemaMarkup,
              local_seo: optimizationResult.localSEO,
              updated_at: new Date().toISOString(),
            })
            .eq("id", postId);

          if (retryError) {
            console.error("❌ Retry also failed:", retryError);
            throw retryError;
          }
        } else {
          throw error;
        }
      } else {
        console.log("✅ Database update successful");
      }

      console.log("Blog post SEO updated successfully");

      // Automatically trigger sitemap regeneration after SEO optimization
      console.log("🔄 Auto-triggering sitemap regeneration...");
      await this.updateSitemap();
    } catch (error) {
      console.error("Error updating blog post SEO:", error);
      // Don't throw error, just log it so the optimization can still complete
      console.log("SEO optimization completed but database update failed");
    }
  }
}
